



























































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import zxcvbn from "zxcvbn";
import { PasswordFormGroupModelI } from "@/models/admin/user-management/PasswordFormGroupModelI";
import { BValidationState } from "@/models/common/BValidationState";

@Component
/**
 * Two Form Groups containing password and password repeat form
 */
export default class PasswordFormGroup extends Vue {
  /**
   * The username which will be deleted
   */
  @Prop({ type: Object, required: true }) value!: PasswordFormGroupModelI;

  /**
   * The label for the first input
   */
  @Prop({ type: String, required: false }) labelFirstPassword!: string;

  /**
   * The min strength of the password complexity as a number from 1-4
   */
  @Prop({ type: String, required: false }) requiredPasswordLevel!: number;

  /**
   * toggle password visibility
   */
  toggleHidePassword(): void {
    this.hidePassword = !this.hidePassword;
  }

  /**
   * indicate if password is hidden in form input
   */
  public hidePassword = true;

  /**
   * the form data
   */
  public form = {
    password: "",
    passwordRepeat: "",
  };

  /**
   * default password strength
   * safely unguessable: moderate protection from offline slow-hash scenario. (guesses < 10^10)
   */
  private static defaultPasswordLevel = 3;

  /**
   * set initial value
   */
  mounted(): void {
    this.form.password = this.value.password;
    if (!this.labelFirstPassword) {
      this.labelFirstPassword = this.$i18n
        .t("components.CreateUserBtnModal.password")
        .toString();
    }
  }

  /**
   * watch and emit password change
   */
  @Watch("form.password", {
    immediate: true,
  })
  passwordChanged(): void {
    this.emitStateChange();
  }

  /**
   * watch and emit passwordRepeat change
   */
  @Watch("form.passwordRepeat", {
    immediate: true,
  })
  passwordRepeatChanged(): void {
    this.emitStateChange();
  }

  /**
   * emit password and validation state
   */
  public emitStateChange(): void {
    this.$emit("input", {
      password: this.form.password,
      valid: this.formValid,
    });
  }

  /**
   * get the correct input type based on show password
   */
  get passwordInputType(): string {
    return this.hidePassword ? "password" : "text";
  }

  /**
   * get the password strength on a scale of 0-4
   */
  get passwordStrength(): number {
    return zxcvbn(this.form.password).score;
  }

  /**
   * get progess bar variant based on password strength
   */
  get passwordProgressVariant(): string {
    const attemptedPasswordStrength = this.requiredPasswordLevel
      ? this.requiredPasswordLevel
      : PasswordFormGroup.defaultPasswordLevel;
    if (this.passwordStrength < attemptedPasswordStrength - 1) {
      return "danger";
    } else if (this.passwordStrength == attemptedPasswordStrength - 1) {
      return "warning";
    } else {
      return "success";
    }
  }

  /**
   * check if password conditions are met for a valid form
   */
  get formValid(): BValidationState {
    if (this.form.password == "" && this.form.passwordRepeat == "") {
      return null;
    }
    return this.passwordValid && this.passwordRepeatValid;
  }

  /**
   * check if password is strong enough
   */
  get passwordValid(): BValidationState {
    if (this.form.password === "") {
      return null;
    }
    const attemptedPasswordStrength = this.requiredPasswordLevel
      ? this.requiredPasswordLevel
      : PasswordFormGroup.defaultPasswordLevel;
    return this.passwordStrength >= attemptedPasswordStrength - 1;
  }

  /**
   * check if password repeat is correct
   */
  get passwordRepeatValid(): BValidationState {
    if (this.form.passwordRepeat === "") {
      return null;
    }
    return (
      this.form.password === this.form.passwordRepeat && this.passwordValid
    );
  }
}
